/**
 * Default stagger for order hero meta items
 * Loosely based on the interval between notes in an announcement sound effect
 * Ref: https://www.youtube.com/watch?v=ii8zdA_teQE&t=4s
 */
const ORDER_HERO_META_STAGGER_MS = 380

/**
 * Extra pause before the checkmark, stepped hero copy, and stacked preview first reveal (ms).
 * Applied to initial gate delays only; does not change stagger spacing between hero lines.
 */
const ORDER_SUCCESS_INITIAL_REVEAL_EXTRA_MS = 600

const ORDER_HERO_META_REVEAL_DELAY_MS = 100 + ORDER_SUCCESS_INITIAL_REVEAL_EXTRA_MS
const ORDER_HERO_META_RING_REVEAL_DELAY_MS = 200 + ORDER_HERO_META_REVEAL_DELAY_MS
/** Icon cluster gate — matches {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.ringRevealDelayMs}. */
const ORDER_HERO_META_ICON_CLUSTER_DELAY_MS = 250 + ORDER_SUCCESS_INITIAL_REVEAL_EXTRA_MS
const ORDER_HERO_META_REVEAL_DURATION_MS = 500
/** Start stacked preview this many ms before the full post-copy hero wait. */
const ORDER_HERO_META_STACKED_PREVIEW_START_EARLIER_MS = 200

/**
 * Delays for the eyebrow text, title text, and description text reveal.
 * Each delay is the sum of the ring reveal delay and the stagger interval multiplied by the index.
 */
const [
    ORDER_HERO_META_EYEBROW_TEXT_REVEAL_DELAY_MS = 0,
    ORDER_HERO_META_TITLE_TEXT_REVEAL_DELAY_MS = 0,
    ORDER_HERO_META_DESCRIPTION_TEXT_REVEAL_DELAY_MS = 0,
] = Array.from(
    { length: 3 },
    (_, i) => ORDER_HERO_META_RING_REVEAL_DELAY_MS + ORDER_HERO_META_STAGGER_MS * (i + 1)
)

const ORDER_HERO_META_COPY_STAGGER_MS =
    ORDER_HERO_META_TITLE_TEXT_REVEAL_DELAY_MS - ORDER_HERO_META_EYEBROW_TEXT_REVEAL_DELAY_MS
/** Subcopy gate — matches {@link OrderHeroMeta} `descriptionRevealDelayMs`. */
const ORDER_HERO_META_DESCRIPTION_REVEAL_GATE_MS =
    ORDER_HERO_META_ICON_CLUSTER_DELAY_MS + ORDER_HERO_META_COPY_STAGGER_MS * 2
const ORDER_HERO_META_STACKED_PREVIEW_REVEAL_HOLD_MS = Math.max(
    0,
    ORDER_HERO_META_DESCRIPTION_REVEAL_GATE_MS +
        ORDER_HERO_META_REVEAL_DURATION_MS -
        ORDER_HERO_META_STACKED_PREVIEW_START_EARLIER_MS
)

/**
 * Default motion timings for order / handwritten-note success and status screens.
 * Override via `app.config` → `orderSuccess` (see {@link useOrderSuccessMotionConfig}).
 */
export const ORDER_SUCCESS_MOTION_DEFAULTS = {
    /**
     * Opacity crossfade when skeleton placeholders swap to live copy in the white status card
     * ({@link OrderStatusCardHeader}, {@link OrderStatusTimelineRow} `<Transition>`).
     */
    skeletonFadeMs: 200,
    /**
     * Opacity transition on timeline row rail markers and icons while the row skeleton overlays
     * ({@link OrderStatusTimelineRow} live content span).
     */
    timelineRowContentFadeMs: 200,
    /** Timings for {@link OrderHeroMeta} checkmark hero, ring, ripple, and stepped copy reveal. */
    heroMeta: {
        /**
         * Duration of the checkmark icon scale/opacity reveal and of each copy line's opacity fade-in (ms).
         */
        revealDurationMs: ORDER_HERO_META_REVEAL_DURATION_MS,
        /**
         * Duration of the outer gold border ring fade-in around the checkmark (ms).
         */
        ringRevealDurationMs: 700,
        /**
         * Delay after `loading` becomes false before the icon and copy lines begin their reveal (ms).
         */
        revealDelayMs: ORDER_HERO_META_REVEAL_DELAY_MS,
        /**
         * Delay before the outer gold ring starts its fade-in, relative to when loading clears (ms).
         */
        ringRevealDelayMs: 250 + ORDER_SUCCESS_INITIAL_REVEAL_EXTRA_MS,
        /**
         * Initial scale of the checkmark icon at the start of the reveal (1 = full size; 0.8 = 80%).
         */
        revealStartScale: 0.8,
        /**
         * Duration of the expanding gold pulse ring played once after the icon finishes revealing (ms).
         */
        iconRippleDurationMs: 1000,
        /**
         * Extra delay after {@link heroMeta.revealDelayMs} before the status eyebrow line fades in (ms).
         */
        eyebrowTextRevealDelayMs: ORDER_HERO_META_EYEBROW_TEXT_REVEAL_DELAY_MS,
        /**
         * Extra delay after {@link heroMeta.revealDelayMs} before the serif title fades in (ms).
         */
        titleTextRevealDelayMs: ORDER_HERO_META_TITLE_TEXT_REVEAL_DELAY_MS,
        /**
         * Extra delay after {@link heroMeta.revealDelayMs} before the description paragraph fades in (ms).
         */
        descriptionTextRevealDelayMs: ORDER_HERO_META_DESCRIPTION_TEXT_REVEAL_DELAY_MS,
    },
    /** Timings for {@link TossedStackedPreview} image gate, spinner, fade mode, and toss-in mode. */
    stackedPreview: {
        /**
         * Parallel floor from when parent `loading` becomes true: reveal waits until this many ms have
         * elapsed **and** images are decoded (`max(load time, hold)` from loading start, ms). `0` disables.
         */
        minRevealHoldFromLoadingMs: ORDER_HERO_META_STACKED_PREVIEW_REVEAL_HOLD_MS,
        /**
         * Extra wait after {@link minRevealHoldFromLoadingMs} and image decode before the reveal gate opens
         * ({@link useRevealDelayGate}, ms). Smooths instant cache hits after the parallel hold clears.
         */
        minRevealDelayMs: 100,
        /**
         * Opacity transition for the centered loading spinner overlay while previews resolve (ms).
         */
        spinnerFadeMs: 300,
        /** Fade-only stack mode (`tossInOnLoad` false): opacity in/out without slab toss keyframes. */
        fade: {
            /**
             * Opacity transition duration while the stack is in the loading/hidden state (ms).
             */
            loadingOpacityMs: 220,
            /**
             * Opacity transition duration when the stack fades in after images are ready (ms).
             */
            revealOpacityMs: 400,
            /**
             * Delay before the post-load opacity fade-in begins (ms).
             */
            revealOpacityDelayMs: 120,
            /**
             * Base stagger interval (seconds) used for stamp pop delay in fade mode
             * (`--toss-stagger` on `.tossed-stacked-preview__stack--fade`).
             */
            staggerSec: 0.2,
        },
        /** Toss-in stack mode (`tossInOnLoad` true): success page card/envelope slab entrance. */
        toss: {
            /**
             * Opacity transition while the whole stack hides during load (ms).
             */
            loadingOpacityMs: 100,
            /**
             * Scale transition while the whole stack shrinks during load (ms).
             */
            loadingScaleMs: 280,
            /**
             * Delay between envelope and front slab toss-in animations (seconds; front runs at 2×).
             */
            staggerSec: 0.2,
            /**
             * Duration of each slab's toss-in keyframe animation (ms).
             */
            slabAnimationMs: 1500,
            /**
             * Duration of the gold letter-mark stamp pop on the envelope (seconds).
             */
            stampPopDurationSec: 0.62,
            /**
             * Stamp animation delay = {@link toss.staggerSec} × this multiplier unless `--stamp-pop-delay` is set.
             */
            stampPopDelayStaggerMultiplier: 6,
        },
    },
} as const

/** Resolved shape returned by {@link useOrderSuccessMotionConfig}. */
export type OrderSuccessMotionConfig = {
    /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.skeletonFadeMs}. */
    skeletonFadeMs: number
    /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.timelineRowContentFadeMs}. */
    timelineRowContentFadeMs: number
    heroMeta: {
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.revealDurationMs}. */
        revealDurationMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.ringRevealDurationMs}. */
        ringRevealDurationMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.revealDelayMs}. */
        revealDelayMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.ringRevealDelayMs}. */
        ringRevealDelayMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.revealStartScale}. */
        revealStartScale: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.iconRippleDurationMs}. */
        iconRippleDurationMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.eyebrowTextRevealDelayMs}. */
        eyebrowTextRevealDelayMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.titleTextRevealDelayMs}. */
        titleTextRevealDelayMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.heroMeta.descriptionTextRevealDelayMs}. */
        descriptionTextRevealDelayMs: number
    }
    stackedPreview: {
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.minRevealHoldFromLoadingMs}. */
        minRevealHoldFromLoadingMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.minRevealDelayMs}. */
        minRevealDelayMs: number
        /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.spinnerFadeMs}. */
        spinnerFadeMs: number
        fade: {
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.fade.loadingOpacityMs}. */
            loadingOpacityMs: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.fade.revealOpacityMs}. */
            revealOpacityMs: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.fade.revealOpacityDelayMs}. */
            revealOpacityDelayMs: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.fade.staggerSec}. */
            staggerSec: number
        }
        toss: {
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.toss.loadingOpacityMs}. */
            loadingOpacityMs: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.toss.loadingScaleMs}. */
            loadingScaleMs: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.toss.staggerSec}. */
            staggerSec: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.toss.slabAnimationMs}. */
            slabAnimationMs: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.toss.stampPopDurationSec}. */
            stampPopDurationSec: number
            /** See {@link ORDER_SUCCESS_MOTION_DEFAULTS.stackedPreview.toss.stampPopDelayStaggerMultiplier}. */
            stampPopDelayStaggerMultiplier: number
        }
    }
}
